// Training: four nodes, three quizzes, attempts, and node completions.
// Approved in TRAINING_NOW_WORDS.md, TRAINING_RECORD_WORDS.md and
// TRAINING_PORTAL_WORDS.md (2026-10-02 and 2026-10-03). Node 4, the field
// demonstration, added in GATES_AT_CUTOVER_WORDS.md (2026-10-05).
//
// Where it lives (company level, beside badges):
//   companies/{company}/quizzes/{quizId}
//   companies/{company}/quizQuestions/{questionId}
//   companies/{company}/quizAttempts/{attemptId}
//   companies/{company}/trainingRecords/{recordId}
// Site rides on each attempt and record as siteNumber. A QEW keeps one
// training history even if he moves sites.
//
// The app never writes any of these. Grading, node closes and Quiz Bank
// edits go through server code, which logs who and when.
import type { Demonstration } from "./field-demo";

export type NodeId = "node1" | "node2" | "node3" | "node4";
export type QuizId = "quiz1" | "quiz2" | "quiz3";

export interface TrainingNode {
  nodeId: NodeId;
  nodeName: string;
  modules: string[];
  /** Node 4 has no quiz. It closes on two demo JSPs. */
  quizId: QuizId | null;
  /** Days a completion stays current. Null: does not expire (Node 4, Robert 2026-10-05). */
  validDays: number | null;
}

/** Approved node table. Node 3's name is a working name. */
export const NODES: readonly TrainingNode[] = [
  { nodeId: "node1", nodeName: "Hazards and ESP", modules: ["TRN-001", "TRN-002"], quizId: "quiz1", validDays: 1095 },
  { nodeId: "node2", nodeName: "Shock, Arc Flash and PPE", modules: ["TRN-003", "TRN-004"], quizId: "quiz2", validDays: 365 },
  { nodeId: "node3", nodeName: "Meters and LOTO", modules: ["TRN-005", "TRN-006"], quizId: "quiz3", validDays: 365 },
  // Node 4 is the field demonstration. No quiz. It closes on two demo JSPs:
  // one Tier 2 troubleshooting JSP and one Tier 3 ESWC JSP (Robert, 2026-10-12).
  // It does not expire; it is redone when the workflow changes (Robert, 2026-10-05).
  {
    nodeId: "node4",
    nodeName: "Field demonstration",
    modules: ["TRN-007", "TRN-008", "TRN-009", "TRN-010"],
    quizId: null,
    validDays: null,
  },
];

/** TRN-007 to TRN-010: Node 4. */
export const FIELD_DEMO_MODULES = ["TRN-007", "TRN-008", "TRN-009", "TRN-010"] as const;

/** Gloves open once these are done, and stay open. The demo stations need gloves, so Node 4 is not here. */
export const GLOVE_NODES: readonly NodeId[] = ["node1", "node2", "node3"];

/** Stored at companies/{company}/quizzes/{quizId}. */
export interface Quiz {
  quizId: QuizId;
  quizName: string;
  nodeId: NodeId;
  slideFrom: number;
  slideTo: number;
  passMarkPct: number;
  /** Missing means on. False stops the quiz page. */
  isActive?: boolean;
  /** Each pass mark change: old, new, who, when. Written by server code. */
  passMarkHistory?: { from: number; to: number; by: string; byName: string; at: string }[];
}

/** Starting values. Pass mark is per quiz, 80 percent (Robert, 2026-10-03). */
export const QUIZZES: readonly Quiz[] = [
  { quizId: "quiz1", quizName: "Quiz 1", nodeId: "node1", slideFrom: 1, slideTo: 24, passMarkPct: 80 },
  { quizId: "quiz2", quizName: "Quiz 2", nodeId: "node2", slideFrom: 1, slideTo: 45, passMarkPct: 80 },
  { quizId: "quiz3", quizName: "Quiz 3", nodeId: "node3", slideFrom: 1, slideTo: 62, passMarkPct: 80 },
];

/** Stored at companies/{company}/quizQuestions/{questionId}. Holds the answer key. */
export interface QuizQuestion {
  questionId: string;
  quizId: QuizId;
  questionNumber: number;
  questionText: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correctAnswer: "A" | "B" | "C" | "D";
  questionType: "MC" | "TF";
  isActive: boolean;
  slideRef: string;
}

/** One answer as the trainee saw it. Kept even if the question changes later. */
export interface AnswerAsSeen {
  questionId: string;
  questionText: string;
  picked: string;
  /**
   * Blank on a missed question of a failed attempt until the correction
   * finishes. The trainee reads his own attempt, so the key stays on the
   * server (quizAttempts/{id}/answerKey/missed) while he corrects.
   */
  correctAnswer: string;
  isCorrect: boolean;
}

/** Stored at companies/{company}/quizAttempts/{attemptId}. Every attempt, pass or fail. */
export interface QuizAttempt {
  attemptId: string;
  qewId: string;
  name: string;
  siteNumber: string;
  quizId: QuizId;
  sessionId: string;
  attemptNumber: number;
  scorePct: number;
  questionsCorrect: number;
  questionsTotal: number;
  passMarkPct: number;
  isPassed: boolean;
  answers: AnswerAsSeen[];
  takenAt: string;
  /** In-class correction on a fail (TRAINEE_QUIZ_WORDS.md, 2026-10-05). Blank on a pass. */
  correctedAt?: string | null;
  correctedScorePct?: number | null;
  /** Per missed question ID, the checked picks in order. Example: { "q2-07": ["B", "A", "D"] }. */
  corrections?: Record<string, string[]>;
  /** Missed question IDs already corrected. */
  correctedQuestionIds?: string[];
  /** Stamped at End Session on a failed attempt. */
  reviewConfirmedBy?: string | null;
  reviewConfirmedAt?: string | null;
}

/** fieldDemo: Node 4 closed itself on the second demo JSP (RECORDS_WORDS.md rev 2). */
export type EarnedBy = "quizPass" | "correctedInClass" | "manualClose" | "carriedOver" | "fieldDemo";

/** Stored at companies/{company}/trainingRecords/{recordId}. One per node closed. */
export interface TrainingRecord {
  recordId: string;
  qewId: string;
  name: string;
  siteNumber: string;
  nodeId: NodeId;
  nodeName: string;
  completedOn: string;
  /** Null when the node does not expire (Node 4). */
  expiresOn: string | null;
  earnedBy: EarnedBy;
  /** The instructor's ID on a manual close. Blank otherwise. */
  closedBy: string;
  sessionId: string;
  quizAttemptId: string;
  /** Entered on a manual close. Blank on a quiz pass (the attempt holds it). */
  scorePct: number | null;
  evidence: string;
  /** Node 4 only: the two demonstration JSPs, in a shape a report can read (RECORDS_WORDS.md rev 2). */
  demonstration?: Demonstration;
  createdAt: string;
}

export function nodeById(nodeId: NodeId): TrainingNode {
  const node = NODES.find((n) => n.nodeId === nodeId);
  if (!node) throw new Error(`Unknown node ${nodeId}`);
  return node;
}

/** Same rounding as the live app: whole percent, rounded. */
export function scorePct(correct: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((correct / total) * 100);
}

/** A score at or above the pass mark passes. */
export function isPassed(correct: number, total: number, passMarkPct: number): boolean {
  return total > 0 && scorePct(correct, total) >= passMarkPct;
}

/** The fewest correct answers that pass. 80 percent of 15 is 12. Of 14 it is still 12. */
export function requiredCorrect(total: number, passMarkPct: number): number {
  for (let n = 0; n <= total; n++) {
    if (isPassed(n, total, passMarkPct)) return n;
  }
  return total + 1;
}

/** Expiry comes from the node, never typed in. Dates are YYYY-MM-DD. Null: does not expire. */
export function expiresOn(completedOn: string, nodeId: NodeId): string | null {
  const date = new Date(`${completedOn}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) throw new Error(`Bad date ${completedOn}`);
  const days = nodeById(nodeId).validDays;
  if (days === null) return null;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** A record counts on a given day when it has no expiry or expires that day or later. */
export function isCurrentOn(expires: string | null | undefined, today: string): boolean {
  return expires === null || expires === undefined || expires === "" || expires >= today;
}

/** Nodes open in order. A node is open when every node before it is done. */
export function nextOpenNode(done: readonly NodeId[]): NodeId | null {
  for (const node of NODES) {
    if (!done.includes(node.nodeId)) return node.nodeId;
  }
  return null;
}

/** All four nodes done: training complete. With LOTO current, the QEW goes Active. */
export function allNodesDone(done: readonly NodeId[]): boolean {
  return NODES.every((n) => done.includes(n.nodeId));
}

/** Nodes 1 to 3 done opens Glove Management. */
export function glovesOpen(done: readonly NodeId[]): boolean {
  return GLOVE_NODES.every((n) => done.includes(n));
}

/**
 * Close by Hand: a date, a score at or above the pass mark, and evidence.
 * Node 4 has no quiz, so it takes a date and evidence only (scored: false).
 */
export function manualCloseProblem(input: {
  completedOn: string;
  correct: number;
  total: number;
  passMarkPct: number;
  evidence: string;
  scored?: boolean;
}): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.completedOn)) return "Enter the date.";
  if (input.scored === false) return input.evidence.trim() ? null : "Enter the evidence.";
  if (input.total <= 0 || input.correct < 0 || input.correct > input.total) return "Enter the score.";
  if (!isPassed(input.correct, input.total, input.passMarkPct)) {
    return `Score is under the pass mark. ${requiredCorrect(input.total, input.passMarkPct)} of ${input.total} needed.`;
  }
  if (!input.evidence.trim()) return "Enter the evidence.";
  return null;
}
